import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Select,
  SegmentedControl,
  Tabs,
  LoadingOverlay,
  Alert,
} from '@mantine/core';
import { IconBrandGoogle, IconAlertCircle } from '@tabler/icons-react';
import { useGoogleAdsConnection, useGoogleAdsAccounts } from '@/hooks/useGoogleAds';
import type { DateRangeOption } from '@/types/googleAds';
import { ConnectionPanel } from './components/ConnectionPanel';
import { OverviewTab } from './tabs/OverviewTab';
import { CampaignsTab } from './tabs/CampaignsTab';
import { KeywordsTab } from './tabs/KeywordsTab';
import { AdsTab } from './tabs/AdsTab';
import { AssetsTab } from './tabs/AssetsTab';
import { AudiencesTab } from './tabs/AudiencesTab';
import { ConversionsTab } from './tabs/ConversionsTab';
import { RecommendationsTab } from './tabs/RecommendationsTab';
import { ExportTab } from './tabs/ExportTab';

const DATE_RANGE_OPTIONS: { value: DateRangeOption; label: string }[] = [
  { value: 'LAST_7_DAYS', label: '7 Tage' },
  { value: 'LAST_30_DAYS', label: '30 Tage' },
  { value: 'LAST_90_DAYS', label: '90 Tage' },
  { value: 'LAST_365_DAYS', label: '365 Tage' },
];

export function GoogleAdsAuditorPage() {
  const { data: connection, isLoading: connectionLoading, error: connectionError } = useGoogleAdsConnection();
  const { data: accounts, isLoading: accountsLoading } = useGoogleAdsAccounts();

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRangeOption>('LAST_30_DAYS');
  const [activeTab, setActiveTab] = useState<string | null>('overview');

  const isConnected = connection?.connected === true;
  const isLoading = connectionLoading || accountsLoading;
  const hasServerError = connectionError && (connectionError as { response?: { status?: number } }).response?.status !== undefined &&
    (connectionError as { response?: { status?: number } }).response!.status! >= 500;

  const accountOptions =
    accounts?.map((account) => ({
      value: account.customerId,
      label: `${account.name} (${account.customerId})`,
    })) ?? [];

  return (
    <Container size="xl" py="xl" pos="relative">
      <LoadingOverlay visible={isLoading} />

      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs">
              <IconBrandGoogle size={32} />
              <Title order={2}>Google Ads Auditor</Title>
            </Group>
            <Text c="dimmed" mt={4}>
              Analysiere und exportiere Google Ads Kontodaten
            </Text>
          </div>
        </Group>

        {hasServerError && (
          <Alert icon={<IconAlertCircle size={16} />} title="Verbindungsfehler" color="red">
            Die Verbindung zum Google Ads Service konnte nicht hergestellt werden. Bitte versuchen Sie es
            später erneut.
          </Alert>
        )}

        <ConnectionPanel connection={connection} isLoading={connectionLoading} />

        {isConnected && (
          <>
            <Group>
              <Select
                label="Konto auswählen"
                placeholder="Google Ads Konto wählen"
                data={accountOptions}
                value={selectedCustomerId}
                onChange={setSelectedCustomerId}
                searchable
                w={350}
              />

              <div>
                <Text size="sm" fw={500} mb={4}>
                  Zeitraum
                </Text>
                <SegmentedControl
                  value={dateRange}
                  onChange={(value) => setDateRange(value as DateRangeOption)}
                  data={DATE_RANGE_OPTIONS}
                />
              </div>
            </Group>

            {selectedCustomerId && (
              <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                  <Tabs.Tab value="overview">Übersicht</Tabs.Tab>
                  <Tabs.Tab value="campaigns">Kampagnen</Tabs.Tab>
                  <Tabs.Tab value="keywords">Keywords</Tabs.Tab>
                  <Tabs.Tab value="ads">Anzeigen</Tabs.Tab>
                  <Tabs.Tab value="assets">Assets</Tabs.Tab>
                  <Tabs.Tab value="audiences">Zielgruppen</Tabs.Tab>
                  <Tabs.Tab value="conversions">Conversions</Tabs.Tab>
                  <Tabs.Tab value="recommendations">Empfehlungen</Tabs.Tab>
                  <Tabs.Tab value="export">Export</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="overview" pt="md">
                  <OverviewTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>

                <Tabs.Panel value="campaigns" pt="md">
                  <CampaignsTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>

                <Tabs.Panel value="keywords" pt="md">
                  <KeywordsTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>

                <Tabs.Panel value="ads" pt="md">
                  <AdsTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>

                <Tabs.Panel value="assets" pt="md">
                  <AssetsTab customerId={selectedCustomerId} />
                </Tabs.Panel>

                <Tabs.Panel value="audiences" pt="md">
                  <AudiencesTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>

                <Tabs.Panel value="conversions" pt="md">
                  <ConversionsTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>

                <Tabs.Panel value="recommendations" pt="md">
                  <RecommendationsTab customerId={selectedCustomerId} />
                </Tabs.Panel>

                <Tabs.Panel value="export" pt="md">
                  <ExportTab customerId={selectedCustomerId} dateRange={dateRange} />
                </Tabs.Panel>
              </Tabs>
            )}

            {!selectedCustomerId && (
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                Bitte wählen Sie ein Google Ads Konto aus, um die Analyse zu starten.
              </Alert>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}
